import React, { useState, useEffect } from 'react';

export default function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isPublicContentOpen, setIsPublicContentOpen] = useState(false);
  const [isPublicContentDropdownOpen, setIsPublicContentDropdownOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
      setIsPublicContentDropdownOpen(false);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'About', href: '#about' },
    { name: 'Experience', href: '#experience' },
    { name: 'Projects', href: '#projects' },
    { name: 'Skills', href: '#skills' },
    { name: 'Contact', href: '#contact' },
  ];

  const publicContentLinks = [
    { name: 'Blogs', href: '#blogs' },
    { name: 'Podcasts', href: '#podcasts' },
    { name: 'Events', href: '#events' },
  ];

  const textColor = isScrolled ? 'text-gray-700' : 'text-white';
  const hoverColor = 'hover:text-primary';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <a href="#hero" className="text-2xl font-bold text-primary">
            AM
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.slice(0, 2).map((link) => (
              <a
                key={link.name}
                href={link.href}
                className={`${textColor} ${hoverColor} transition font-medium`}
              >
                {link.name}
              </a>
            ))}

            {/* Public Content Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setIsPublicContentDropdownOpen(true)}
              onMouseLeave={() => setIsPublicContentDropdownOpen(false)}
            >
              <button
                className={`flex items-center gap-1 ${textColor} ${hoverColor} transition font-medium`}
                aria-expanded={isPublicContentDropdownOpen}
                aria-haspopup="true"
              >
                Public Content
                <svg
                  className={`w-4 h-4 transition-transform ${isPublicContentDropdownOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isPublicContentDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 py-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200">
                  {publicContentLinks.map((link) => (
                    <a
                      key={link.name}
                      href={link.href}
                      className="block px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-primary transition"
                    >
                      {link.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {navLinks.slice(2).map((link) => (
              <a
                key={link.name}
                href={link.href}
                className={`${textColor} ${hoverColor} transition font-medium`}
              >
                {link.name}
              </a>
            ))}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <svg
              className={`w-6 h-6 ${textColor}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {isMobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navLinks.slice(0, 2).map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="block px-3 py-2 text-gray-700 hover:text-primary hover:bg-gray-50 rounded-md"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.name}
              </a>
            ))}

            {/* Public Content - Expandable */}
            <div>
              <button
                onClick={() => setIsPublicContentOpen(!isPublicContentOpen)}
                className="flex items-center justify-between w-full px-3 py-2 text-gray-700 hover:text-primary hover:bg-gray-50 rounded-md"
              >
                Public Content
                <svg
                  className={`w-4 h-4 transition-transform ${isPublicContentOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isPublicContentOpen && (
                <div className="pl-4 mt-1 space-y-1">
                  {publicContentLinks.map((link) => (
                    <a
                      key={link.name}
                      href={link.href}
                      className="block px-3 py-2 text-gray-600 hover:text-primary hover:bg-gray-50 rounded-md text-sm"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {link.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {navLinks.slice(2).map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="block px-3 py-2 text-gray-700 hover:text-primary hover:bg-gray-50 rounded-md"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.name}
              </a>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}
